import {
  AlertTriangle,
  ArrowRight,
  Clock3,
  FileWarning,
  type LucideIcon,
} from "lucide-react";

export type ChurchAttentionSeverity =
  | "HIGH"
  | "MEDIUM";

export type ChurchAttentionType =
  | "INVITATION"
  | "MIGRATION"
  | "ONBOARDING";

export interface ChurchAttentionItem {
  id: string;
  church_name: string;
  issue: string;
  next_action: string;
  severity: ChurchAttentionSeverity;
  type: ChurchAttentionType;
}

interface ChurchesNeedingAttentionCardProps {
  items?: ChurchAttentionItem[];
  onSelectItem?: (
    item: ChurchAttentionItem
  ) => void;
}

interface AttentionTypeStyle {
  icon: LucideIcon;
  iconClassName: string;
}

const typeStyles: Record<
  ChurchAttentionType,
  AttentionTypeStyle
> = {
  INVITATION: {
    icon: Clock3,
    iconClassName:
      "bg-amber-50 text-amber-600",
  },
  MIGRATION: {
    icon: FileWarning,
    iconClassName:
      "bg-violet-50 text-violet-600",
  },
  ONBOARDING: {
    icon: AlertTriangle,
    iconClassName:
      "bg-rose-50 text-rose-600",
  },
};

const severityClasses: Record<
  ChurchAttentionSeverity,
  string
> = {
  HIGH: "bg-rose-50 text-rose-700",
  MEDIUM: "bg-amber-50 text-amber-700",
};

const severityLabels: Record<
  ChurchAttentionSeverity,
  string
> = {
  HIGH: "High",
  MEDIUM: "Medium",
};

export function ChurchesNeedingAttentionCard({
  items = [],
  onSelectItem,
}: ChurchesNeedingAttentionCardProps) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-950">
            Needs attention
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Pilot churches with blocked or overdue
            tasks.
          </p>
        </div>

        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
          <AlertTriangle className="h-5 w-5" />
        </div>
      </div>

      {items.length > 0 ? (
        <div className="divide-y divide-slate-100">
          {items.map((item) => (
            <AttentionItemRow
              key={item.id}
              item={item}
              onSelectItem={onSelectItem}
            />
          ))}
        </div>
      ) : (
        <EmptyAttentionState />
      )}
    </section>
  );
}

interface AttentionItemRowProps {
  item: ChurchAttentionItem;
  onSelectItem?: (
    item: ChurchAttentionItem
  ) => void;
}

function AttentionItemRow({
  item,
  onSelectItem,
}: AttentionItemRowProps) {
  const style = typeStyles[item.type];
  const Icon = style.icon;

  return (
    <article className="p-5">
      <div className="flex gap-4">
        <div
          className={[
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
            style.iconClassName,
          ].join(" ")}
        >
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-slate-900">
              {item.church_name}
            </p>

            <span
              className={[
                "rounded-full px-2.5 py-1 text-xs font-semibold",
                severityClasses[item.severity],
              ].join(" ")}
            >
              {severityLabels[item.severity]}
            </span>
          </div>

          <p className="mt-2 text-sm text-slate-600">
            {item.issue}
          </p>

          <button
            type="button"
            onClick={() => {
              onSelectItem?.(item);
            }}
            disabled={!onSelectItem}
            className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-indigo-600 transition hover:text-indigo-500 disabled:cursor-default disabled:text-slate-400"
          >
            {item.next_action}
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </article>
  );
}

function EmptyAttentionState() {
  return (
    <div className="px-6 py-10 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
        <AlertTriangle className="h-6 w-6" />
      </div>

      <p className="mt-4 text-sm font-semibold text-slate-900">
        No churches need attention
      </p>

      <p className="mt-1 text-sm text-slate-500">
        Invitation, migration, and onboarding issues
        will appear here when attention is required.
      </p>
    </div>
  );
}